/**
 * Pure decision helper for the live-build-preview AUTO-OPEN behaviour
 * (#1408 §9 Phase 0 / #1420 §5.1).
 *
 * The Workspace switches to `workspaceMode = 'preview'` the FIRST time
 * an agent (build) turn starts for the active game — but only when the
 * `livePreview` feature flag is on AND the user has not opted out of
 * auto-open. `chat.isAiTyping` (the renderer's view of the
 * `agent:stream` turn-start, `chat/state.ts`) is the build-turn-active
 * signal.
 *
 * Extracted as a pure function (mirroring the `config-get`/`config-set`
 * extractions) so the gating logic is unit-tested directly instead of
 * by mounting the ~8k-line Workspace. The Workspace owns the per-game
 * latch (`alreadyAutoOpenedFor`) and the workspace-mode side effect;
 * this function owns only the decision.
 *
 * FLAG-OFF / OPTED-OUT IS A STRICT NO-OP: `livePreview === false`
 * short-circuits before anything else, so flag-OFF behaviour is
 * byte-identical to today (the preview stays a manual click and the
 * `livePreviewAutoOpen` preference is never consulted). This is the
 * Phase-0 flag-parity guarantee (#1420 §12).
 */
export interface AutoOpenPreviewInput {
  /** `useFeatureFlags().loaded` — false during the in-flight window
   *  before `app:get-feature-flags` resolves. We never auto-open
   *  before the flag value is known. */
  flagsLoaded: boolean;
  /** `useFeatureFlags().livePreview` — the feature gate. */
  livePreview: boolean;
  /** `config:get`'s `livePreviewAutoOpen` (the per-user opt-out).
   *  `undefined` (fresh install / older sidecar) means the default,
   *  which is ON. Only an explicit `false` opts out. */
  livePreviewAutoOpen: boolean | undefined;
  /** The active route's gameId, or null/undefined when none. */
  gameId: string | null | undefined;
  /** `chat.isAiTyping` — true when an agent turn is in flight. */
  isAiTyping: boolean;
  /** `workspaceRunState.kind === 'build-kickoff-pending'` — after Start
   *  Building / Rebuild the UI stays on chat until Phase B starts; do not
   *  auto-flip to preview while the kickoff sentinel has `isAiTyping`. */
  buildKickoffPending: boolean;
  /** `isActiveInitialKickoffStatus(initialKickoff.status)` — true while
   *  the INTERVIEW kickoff is pre-dispatch (`waiting-for-renderer-ready`
   *  / `dispatching`). The autofired initial prompt sets `isAiTyping=true`
   *  via `startTurn` BEFORE the `agent:send` dispatches, and the dispatch
   *  is itself held until the `InterviewSlotOverlay` mounts and fires
   *  `onRendererReady` — but that overlay is only rendered when
   *  `workspaceMode !== 'preview'`. Auto-opening here therefore unmounts
   *  the very surface the kickoff is waiting on: renderer-ready never
   *  fires, the hold never releases, and the kickoff dies on the 15s
   *  `INITIAL_KICKOFF_TIMEOUT_MS` budget ("Interview did not start").
   *  Suppress until the kickoff has dispatched (or settled idle). */
  initialKickoffActive: boolean;
  /** The set of gameIds the Workspace has ALREADY auto-opened the
   *  preview for this session. Prevents re-opening (and fighting a user
   *  who closed the preview mid-build) — auto-open fires at most once
   *  per game. A SET (not a single slot) because the Workspace persists
   *  across game switches: with a single slot, opening game B would
   *  forget that the user had closed game A's preview, so returning to
   *  A's still-running build would wrongly re-open it. */
  autoOpenedGames: ReadonlySet<string>;
}

/**
 * Returns true iff the Workspace should auto-open the live preview for
 * the active game right now. The caller, on true, records the gameId in
 * its per-game latch and switches Workspace to Preview mode.
 */
export function shouldAutoOpenPreview(input: AutoOpenPreviewInput): boolean {
  // Flag gate FIRST — flag-OFF is a strict no-op (never consults the
  // auto-open preference, never opens).
  if (!input.livePreview) return false;
  // Don't act before the flag snapshot has resolved.
  if (!input.flagsLoaded) return false;
  // Need a concrete game to open a preview for.
  const { gameId } = input;
  if (!gameId) return false;
  // Opt-out: only an explicit `false` suppresses; undefined = default ON.
  if (input.livePreviewAutoOpen === false) return false;
  // Only when an agent (build) turn is actually in flight.
  if (!input.isAiTyping) return false;
  // Committed-plan handoff: chat-first until Phase B (not kickoff sentinel).
  if (input.buildKickoffPending) return false;
  // Interview kickoff hold: the initial agent:send is parked until the
  // interview overlay mounts and signals renderer-ready — an overlay
  // that only renders outside preview mode. Flipping to preview now
  // would deadlock the kickoff into its timeout (see field doc above).
  if (input.initialKickoffActive) return false;
  // At most once per game — don't re-open if we already auto-opened for
  // this game (the user may have deliberately closed it).
  if (input.autoOpenedGames.has(gameId)) return false;
  return true;
}
